import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { useNavigate } from 'react-router-dom';
import { Loader2, Folder, FileText, CheckCircle2, AlertCircle, Clock, PauseCircle, Ban, ExternalLink, XCircle, Copy, PlayCircle, IndianRupee, Info, ShieldAlert, Send, Mail, FileSpreadsheet, FileCheck, RotateCcw, TrendingUp } from 'lucide-react';
import { IdeasFilterTable } from '../components/IdeasFilterTable.tsx';
import { AmountDisplay } from '../components/AmountDisplay.tsx';
import { CountCardHint } from '../components/CountCardHint.tsx';
import { formatIndianCurrency } from '../utils/currency.ts';

import { BulkAssignModal } from '../components/BulkAssignModal.tsx';
import { BulkEvaluateImportModal } from '../components/BulkEvaluateImportModal.tsx';
import { IdeaItem } from '../components/IdeasFilterTable.tsx';
import { useSessionState } from '../hooks/useSessionState.ts';
import { useState, useRef, useEffect } from 'react';

export function CommitteeHeadDashboard({ showFuturePotentialByEc = true }: { showFuturePotentialByEc?: boolean } = {}) {
  const navigate = useNavigate();
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [isEvalImportModalOpen, setIsEvalImportModalOpen] = useState(false);
  const [bulkSelectedIdeas, setBulkSelectedIdeas] = useState<IdeaItem[]>([]);
  const [showAssignToast, setShowAssignToast] = useState(false);
  const [clearBulkSelectionFn, setClearBulkSelectionFn] = useState<(() => void) | null>(null);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useSessionState<string[]>('committeeHead_selectedAssigneeIds', []);
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const memberDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: committeeMembers } = useQuery({
    queryKey: ['committeeMembersList'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/ideas/dashboard-eligible-assignees');
      return res.data.data || [];
    }
  });

  const { data: globalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats', selectedAssigneeIds.join(',')],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats', {
        params: { assigneeId: selectedAssigneeIds.join(',') || undefined }
      });
      return res.data.data;
    },
  });

  return (
    <Layout title="Committee Head Portal">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800">DOM680 Dashboard</h2>
          <p className="text-slate-500 text-sm">Review, prioritize and moderate idea workflow</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate('/admin/bulk-evaluate-import')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 sm:px-3.5 py-1.5 sm:py-1.5 rounded-lg text-xs transition-all flex items-center justify-center space-x-1.5 cursor-pointer shadow-sm shadow-blue-600/10 border-0"
          >
            <FileCheck className="w-4 h-4" />
            <span>Bulk Evaluation Import</span>
          </button>
          <div className="flex items-center space-x-2" ref={memberDropdownRef}>
            <span className="text-sm font-medium text-slate-600 shrink-0">Committee Member:</span>
            <div className="relative">
              <button
                onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}
                className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px] text-left flex justify-between items-center"
              >
                <span className="truncate max-w-[150px]">
                  {selectedAssigneeIds.length === 0
                    ? 'All Members'
                    : selectedAssigneeIds.length === 1
                      ? committeeMembers?.find((m: any) => m.id === selectedAssigneeIds[0])?.name || 'Selected (1)'
                      : `Selected (${selectedAssigneeIds.length})`}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 ml-2"><path d="m6 9 6 6 6-6" /></svg>
              </button>
              {isMemberDropdownOpen && (
                <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[220px] w-max max-w-xs bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto p-1">
                  <label className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer rounded-lg text-sm">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 mr-2"
                      checked={selectedAssigneeIds.length === 0}
                      onChange={() => setSelectedAssigneeIds([])}
                    />
                    <span className="text-slate-700 font-medium">All Members (Clear)</span>
                  </label>
                  <label className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer rounded-lg text-sm border-b border-slate-100">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 mr-2"
                      checked={committeeMembers && committeeMembers.length > 0 && selectedAssigneeIds.length === committeeMembers.length}
                      onChange={(e) => {
                        if (e.target.checked && committeeMembers) {
                          setSelectedAssigneeIds(committeeMembers.map((m: any) => m.id));
                        } else {
                          setSelectedAssigneeIds([]);
                        }
                      }}
                    />
                    <span className="text-slate-700 font-medium">Check All</span>
                  </label>
                  {committeeMembers?.map((m: any) => (
                    <label key={m.id} className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer rounded-lg text-sm">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 mr-2"
                        checked={selectedAssigneeIds.includes(m.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAssigneeIds(prev => [...prev, m.id]);
                          } else {
                            setSelectedAssigneeIds(prev => prev.filter(id => id !== m.id));
                          }
                        }}
                      />
                      <span className="text-slate-700">{m.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {selectedAssigneeIds.length > 0 && (
              <button
                onClick={() => setSelectedAssigneeIds([])}
                className="bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 font-semibold px-2.5 py-1.5 rounded-lg text-xs transition-all flex items-center space-x-1 cursor-pointer border border-slate-300 shadow-sm shrink-0"
                title="Reset member filter"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {statsLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
            {/* 1) Total Ideas */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600 shrink-0">
                  <Folder className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Total Ideas</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.totalIdeas || 0}</p>
            </div>

            {/* 2) Hold */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                  <ShieldAlert className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Hold</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.infoRequested || 0}</p>
            </div>

            {/* 3) Duplicate */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-slate-100 p-1.5 rounded-lg text-slate-600 shrink-0">
                  <Copy className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Duplicate</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.duplicateCount || 0}</p>
            </div>

            {/* 4) Rejected */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-red-50 p-1.5 rounded-lg text-red-600 shrink-0">
                  <Ban className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Rejected</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.rejected || 0}</p>
            </div>



            {/* 5) Reviewed */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-orange-50 p-1.5 rounded-lg text-orange-600 shrink-0">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Reviewed</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.underEvaluation || 0}</p>
            </div>

            {/* 6) Yet to Be Reviewed */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                  <Send className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Yet to Be Reviewed</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.submitted || 0}</p>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Approved by MIS">Approved by MIS</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.misApprovedCount || 0}</p>
                </div>
                <CountCardHint text="Ideas the MIS team has approved. The idea status stays the same." />
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-rose-50 p-1.5 rounded-lg text-rose-600 shrink-0">
                  <Ban className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Rejected by MIS">Rejected by MIS</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.misRejectedCount || 0}</p>
                </div>
                <CountCardHint text="Ideas the MIS team has rejected. A rejected idea is stored as a duplicate." />
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Assigned Ideas</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.assignedIdeasCount || 0}</p>
                </div>
                <CountCardHint text="Ideas that already have a committee member assigned." />
              </div>
            </div>

            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-slate-50 p-1.5 rounded-lg text-slate-600 shrink-0">
                  <Ban className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Unassigned Ideas">Unassigned Ideas</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.unassignedIdeasCount || 0}</p>
                </div>
                <CountCardHint text="Ideas that do not have a committee member assigned yet." />
              </div>
            </div>

            {/* 7) WIP – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-cyan-50 p-1.5 rounded-lg text-cyan-600 shrink-0">
                  <PlayCircle className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">WIP</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.workInProgressCount || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.wipTotalAmount || 0} textSize="text-[13px]" />
                </div>
              </div>
            </div>

            {/* 8) Pending IC Head Approval – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 shrink-0">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Pending IC Head Approval</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.pendingHeadApproval || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.pendingHeadApprovalValue || 0} textSize="text-[13px]" />
                </div>
              </div>
            </div>

            {/* 9) Requested EC Head Approval – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-fuchsia-50 p-1.5 rounded-lg text-fuchsia-600 shrink-0">
                  <ExternalLink className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Requested EC Head Approval</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.pendingExternalApprovalValue || 0} textSize="text-[13px]" />
                </div>
              </div>
            </div>

            {/* 10) EC Head Approved – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-green-50 p-1.5 rounded-lg text-green-600 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Approved</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.approved || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.actualExternalApprovedAmount || 0} textSize="text-[13px]" />
                </div>
              </div>
            </div>

            {showFuturePotentialByEc && (
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Future potential by EC</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">
                    {/* OLD: Requested − (Approved + Email Confirmed)
                    {(globalStats?.kpis?.pendingExternalApproval || 0) -
                      ((globalStats?.kpis?.approved || 0) + (globalStats?.kpis?.emailConfirmedCount || 0))}
                    */}
                    {globalStats?.kpis?.futurePotentialByEcCount || 0}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay
                    amount={
                      /* OLD: Requested − (Approved + Email Confirmed)
                      (globalStats?.kpis?.pendingExternalApprovalValue || 0) -
                      ((globalStats?.kpis?.actualExternalApprovedAmount || 0) +
                        (globalStats?.kpis?.emailConfirmationAnnualSavings || 0))
                      */
                      globalStats?.kpis?.futurePotentialByEcValue || 0
                    }
                    textSize="text-[13px]"
                    forceCrores={true}
                  />
                </div>
              </div>
            </div>
            )}

            {/* 11) EC Head Rejected – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-rose-50 p-1.5 rounded-lg text-rose-600 shrink-0">
                  <XCircle className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Rejected</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.externalRejected || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.externalRejectedValue || 0} textSize="text-[13px]" />
                </div>
              </div>
            </div>

            {/* 12) EC Head Email Confirmed – Nos & Value */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Email Confirmed</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.emailConfirmedCount || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.emailConfirmationAnnualSavings || 0} textSize="text-[13px]" />
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      <IdeasFilterTable
        queryKeyPrefix="headIdeas"
        apiEndpoint="/admin/ideas"
        showExport={true}
        showFuturePotentialByEc={showFuturePotentialByEc}
        allowDelete={false}
        allowBulkAssign={true}
        onBulkAssign={(selectedIdeas, clearSelection) => {
          setBulkSelectedIdeas(selectedIdeas);
          setClearBulkSelectionFn(() => clearSelection);
          setIsBulkAssignModalOpen(true);
        }}
        actionButtonLabel="Evaluate"
        includeMisStatuses
        assigneeId={selectedAssigneeIds.join(',') || undefined}
      />

      <BulkAssignModal
        isOpen={isBulkAssignModalOpen}
        onClose={() => {
          setIsBulkAssignModalOpen(false);
          setBulkSelectedIdeas([]);
        }}
        selectedIdeas={bulkSelectedIdeas}
        onSuccess={() => {
          setShowAssignToast(true);
          setTimeout(() => setShowAssignToast(false), 3000);
          if (clearBulkSelectionFn) {
            clearBulkSelectionFn();
          }
        }}
      />

      {showAssignToast && (
        <div className="fixed top-24 right-6 bg-emerald-600 text-white px-5 py-3.5 rounded-xl shadow-lg shadow-emerald-600/30 flex items-center space-x-3 z-50 animate-in slide-in-from-top-5 duration-300">
          <div className="bg-white/20 rounded-full p-1">
            <CheckCircle2 className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-sm tracking-wide">Ideas successfully assigned!</span>
        </div>
      )}

      <BulkEvaluateImportModal
        isOpen={isEvalImportModalOpen}
        onClose={() => setIsEvalImportModalOpen(false)}
      />
    </Layout>
  );
}
